/**
 * Sistema de Feature Gating por Licencia
 * Controla qué funcionalidades están disponibles según el tipo de licencia
 */

export type LicenseType = 'familia' | 'educacion';

export interface FeatureFlags {
  // Funcionalidades de Logrit Familia
  routines: boolean;            // Sistema de Rutinas/Temporadas (BÁSICO)
  templates: boolean;           // Biblioteca de rutinas
  marketplace: boolean;          // Marketplace de recompensas
  familyStats: boolean;         // Estadísticas familiares
  challenges: boolean;          // Desafíos entre miembros
  recipes: boolean;             // Sistema de recetas
  mealPlanning: boolean;        // Planificación de comidas
  shoppingLists: boolean;       // Listas de compras
  
  // Funcionalidades de Logrit Educa
  attendance: boolean;          // Registro de asistencia
  grades: boolean;              // Sistema de calificaciones
  evaluations: boolean;         // Evaluaciones personalizadas
  reports: boolean;             // Reportes académicos
  resources: boolean;           // Adjuntar recursos a actividades
  bulletin: boolean;            // Boletín de calificaciones
  subjects: boolean;            // Gestión de materias/asignaturas
  gradePeriods: boolean;        // Períodos de evaluación (trimestre, etc.)
}

/**
 * Configuración de features por tipo de licencia
 */
export const FEATURES_BY_LICENSE: Record<LicenseType, FeatureFlags> = {
  familia: {
    // Habilitado en Familia (BÁSICO)
    routines: true,             // ✅ Sistema de Rutinas/Temporadas (BÁSICO)
    templates: false,           // ❌ Biblioteca pausada (usar Rutinas)
    marketplace: true,
    familyStats: true,
    challenges: true,
    recipes: true,              // ✅ Sistema de recetas
    mealPlanning: true,         // ✅ Planificación de comidas
    shoppingLists: true,        // ✅ Listas de compras
    
    // Deshabilitado en Familia
    attendance: false,
    grades: false,
    evaluations: false,
    reports: false,
    resources: false,
    bulletin: false,
    subjects: false,
    gradePeriods: false,
  },
  
  educacion: {
    // Deshabilitado en Educación
    routines: false,            // ❌ No aplica en educación
    templates: false,           // Las rutinas no aplican en contexto educativo
    marketplace: false,         // El marketplace se puede adaptar luego
    familyStats: false,         // Estadísticas son diferentes en educación
    challenges: false,          // Los desafíos no aplican en educación
    recipes: false,             // ❌ No disponible en educación
    mealPlanning: false,        // ❌ No disponible en educación
    shoppingLists: false,       // ❌ No disponible en educación
    
    // Habilitado en Educación
    attendance: true,
    grades: true,
    evaluations: true,
    reports: true,
    resources: true,
    bulletin: true,
    subjects: true,
    gradePeriods: true,
  },
};

/**
 * Obtiene las features disponibles para un tipo de licencia
 */
export function getFeatures(licenseType: LicenseType = 'familia'): FeatureFlags {
  return FEATURES_BY_LICENSE[licenseType];
}

/**
 * Verifica si una feature específica está habilitada
 */
export function hasFeature(
  licenseType: LicenseType = 'familia',
  feature: keyof FeatureFlags
): boolean {
  return FEATURES_BY_LICENSE[licenseType][feature];
}

/**
 * Períodos de evaluación disponibles
 */
export type GradePeriodType = 'monthly' | 'quarterly' | 'fourMonths' | 'semester' | 'annual';

export interface GradePeriodConfig {
  id: GradePeriodType;
  label: string;
  labelPlural: string;
  months: number;
  periodsPerYear: number;
  emoji: string;
}

export const GRADE_PERIODS: Record<GradePeriodType, GradePeriodConfig> = {
  monthly: {
    id: 'monthly',
    label: 'Mes',
    labelPlural: 'Meses',
    months: 1,
    periodsPerYear: 12,
    emoji: '📅',
  },
  quarterly: {
    id: 'quarterly',
    label: 'Trimestre',
    labelPlural: 'Trimestres',
    months: 3,
    periodsPerYear: 4,
    emoji: '📊',
  },
  fourMonths: {
    id: 'fourMonths',
    label: 'Cuatrimestre',
    labelPlural: 'Cuatrimestres',
    months: 4,
    periodsPerYear: 3,
    emoji: '📈',
  },
  semester: {
    id: 'semester',
    label: 'Semestre',
    labelPlural: 'Semestres',
    months: 6,
    periodsPerYear: 2,
    emoji: '🎓',
  },
  annual: {
    id: 'annual',
    label: 'Año',
    labelPlural: 'Años',
    months: 12,
    periodsPerYear: 1,
    emoji: '📚',
  },
};

/**
 * Escala de calificaciones
 */
export type GradeScale = 'numeric_100' | 'numeric_10' | 'letter' | 'pass_fail';

export interface GradeScaleConfig {
  id: GradeScale;
  label: string;
  min: number;
  max: number;
  passingGrade: number;
  decimals: boolean;
}

export const GRADE_SCALES: Record<GradeScale, GradeScaleConfig> = {
  numeric_100: {
    id: 'numeric_100',
    label: '0-100',
    min: 0,
    max: 100,
    passingGrade: 60,
    decimals: true,
  },
  numeric_10: {
    id: 'numeric_10',
    label: '0-10',
    min: 0,
    max: 10,
    passingGrade: 6,
    decimals: true,
  },
  letter: {
    id: 'letter',
    label: 'A-F',
    min: 0,
    max: 4, // A=4, B=3, C=2, D=1, F=0
    passingGrade: 2,
    decimals: false,
  },
  pass_fail: {
    id: 'pass_fail',
    label: 'Aprobado/Reprobado',
    min: 0,
    max: 1,
    passingGrade: 1,
    decimals: false,
  },
};

/**
 * Convierte calificación numérica a letra
 */
export function numericToLetter(grade: number): string {
  if (grade >= 90) return 'A';
  if (grade >= 80) return 'B';
  if (grade >= 70) return 'C';
  if (grade >= 60) return 'D';
  return 'F';
}

/**
 * Convierte letra a valor numérico
 */
export function letterToNumeric(letter: string): number {
  const map: Record<string, number> = { A: 4, B: 3, C: 2, D: 1, F: 0 };
  return map[letter.toUpperCase()] ?? 0;
}
